/**
 * Builds GraphQL root-field args and operation varDefs from `variables` + `typeMap`.
 * Rules:
 * - Include keys where value !== undefined (null is allowed and included).
 * - Every included key must exist in `typeMap` (e.g. { id: "ID!", flag: "Boolean" }).
 * - Variable names must match /^[A-Za-z_]\w*$/.
 * - Keys are sorted for deterministic output.
 * - If no keys -> returns empty args and operation variable definitions.
 */
export function buildArgsAndVarDefs(variables = {}, typeMap = {}) {
    if (variables == null || typeof variables !== 'object') {
        throw new Error("buildArgsAndVarDefs: 'variables' must be an object");
    }
    if (typeMap == null || typeof typeMap !== 'object') {
        throw new Error("buildArgsAndVarDefs: 'typeMap' must be an object");
    }

    const keys = Object.keys(variables).filter((k) => variables[k] !== undefined);

    if (keys.length === 0) return { argsStr: "", varDefsStr: "" };

    const missing = keys.filter((k) => !typeMap[k]);
    if (missing.length) {
        throw new Error(
            `buildArgsAndVarDefs: missing GraphQL types in typeMap for keys: ${missing.join(", ")}`
        );
    }

    const invalid = keys.filter((k) => !/^[A-Za-z_]\w*$/.test(k));
    if (invalid.length) {
        throw new Error(
            `buildArgsAndVarDefs: invalid variable names: ${invalid.join(", ")}`
        );
    }

    keys.sort();

    const varDefs = keys.map((k) => `$${k}: ${String(typeMap[k]).trim()}`);
    const argPairs = keys.map((k) => `${k}: $${k}`);
    const args = `(${argPairs.join(", ")})`;

    const varDefsStr = varDefs.length ? `(${varDefs.join(", ")})` : "";
    const argsStr = args || "";

    return { argsStr, varDefsStr };
}

export default { buildArgsAndVarDefs };
